import React, { useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Button, BackLink, ButtonWrapper, Link } from "ds/Button";
import Dialog from "ds/Dialog";
import Modal from "ds/Modal";
import Tag from "ds/Tag";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { environmentSelector } from "Reducers/environment";
import { gitProjectSelector } from "Reducers/project/project";
import { useLoadDomain } from "Reducers/project/settings/domains/hooks/useLoadDomain";
import { useDeleteEnvironmentDomain } from "Reducers/project/settings/environmentDomains/hooks/useDeleteEnvironmentDomain";
import { useLoadEnvironmentDomains } from "Reducers/project/settings/environmentDomains/hooks/useLoadEnvironmentDomains";
import ModalConfirmDelete from "src/organization/pages/project/settings/domains/components/DeleteModal";

import * as S from "./EnvironmentDomainView.styles";

export type AddEnvironmentDomainFormDataProps = {
  environmentDomainName: string;
  replacementDomain: string;
  environmentId: string;
};

const EnvironmentDomainView = () => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const navigate = useNavigate();

  const environment = useSelectorWithUrlParams(environmentSelector);
  const project = useSelectorWithUrlParams(gitProjectSelector);

  const { handleDelete } = useDeleteEnvironmentDomain(project, environment);
  const { handleSetDefault, hasDomainPermission } = useLoadDomain(project);

  const intl = useIntl();

  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();

  const customDomain = window.location.href.split("/").pop();

  const encodedEnvironmentId = encodeURIComponent(environmentId);

  const closeModals = () => {
    setShowDeleteModal(false);
  };

  const customDomainsView = () => {
    navigate(
      `/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains`
    );
  };

  const { environmentDomains } = useLoadEnvironmentDomains(
    environment,
    project
  );

  const environmentDomain = environmentDomains?.find(
    environment => environment.id === customDomain
  );

  const isProductionEnvironment = environment?.type === "production";

  const defaultDomain = project?.default_domain === environmentDomain?.name;

  return (
    <S.Layout>
      <S.Container>
        <BackLink
          id="project-domains-back-button"
          from={`/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains/-/view`}
          variant="link"
          to={`/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains`}
          capitalized={false}
        >
          {intl.formatMessage({ id: "domains.back_to" })}
        </BackLink>
        <S.Header>
          {customDomain}

          {defaultDomain && (
            <Tag variant="purple">
              {intl.formatMessage({
                id: "settings.domains.default"
              })}
            </Tag>
          )}
        </S.Header>

        <S.AddDomainFormLayout
          id="domain-add-form"
          aria-labelledby="add-domain-heading"
        >
          <S.MainContainer>
            <strong>
              {capitalize(intl.formatMessage({ id: "settings.details" }))}
            </strong>

            {!isProductionEnvironment && (
              <Dialog
                analyticId="domains.details.learn_more"
                description={intl.formatMessage({
                  id: "domains.details.learn_more",
                  defaultMessage:
                    "You can’t edit a custom non-production domain. You can only delete it and create a new one as a replacement."
                })}
                align="center"
                link={
                  <Link
                    analyticId="learnmore"
                    href={intl.formatMessage({
                      id: "links.domains.replacement"
                    })}
                  >
                    {intl.formatMessage({ id: "learnmore" })}
                  </Link>
                }
              />
            )}
          </S.MainContainer>

          <S.LabelText>
            {capitalize(intl.formatMessage({ id: "domain" }))}
          </S.LabelText>
          <S.DefaultView>
            <S.Text>{customDomain}</S.Text>
            {defaultDomain && (
              <Tag variant="purple">
                {intl.formatMessage({
                  id: "settings.domains.default"
                })}
              </Tag>
            )}
          </S.DefaultView>
          <S.LabelText>
            {capitalize(intl.formatMessage({ id: "domains.environment" }))}
          </S.LabelText>
          <S.Text>{environment?.id}</S.Text>

          {!isProductionEnvironment && (
            <>
              <S.LabelContainer>
                {capitalize(
                  intl.formatMessage({ id: "domains.replacement_domain" })
                )}

                <Dialog
                  analyticId="domains.replacement_domain.info"
                  description={intl.formatMessage({
                    id: "domains.replacement_domain.info",
                    defaultMessage:
                      "For the routing of your app, your production domain maps to this non-production domain."
                  })}
                  align="center"
                  link={
                    <Link
                      analyticId="learnmore"
                      href={intl.formatMessage({
                        id: "links.domains.replacement"
                      })}
                    >
                      {intl.formatMessage({ id: "learnmore" })}
                    </Link>
                  }
                />
              </S.LabelContainer>
              <S.Text>{environmentDomain?.replacement_for}</S.Text>
            </>
          )}
          <ButtonWrapper>
            <>
              {project?.default_domain !== environmentDomain?.name &&
                isProductionEnvironment && (
                  <>
                    <Button
                      analyticId="settings.domains.make_default"
                      disabled={!hasDomainPermission}
                      onClick={() => {
                        if (environmentDomain?.id) {
                          handleSetDefault(environmentDomain.id);
                        }
                      }}
                    >
                      {capitalize(
                        intl.formatMessage({
                          id: "settings.domains.make_default"
                        })
                      )}
                    </Button>
                  </>
                )}

              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(true)}
                data-testid="project-domain-cancel-btn"
                analyticId="settings.domains.delete"
                disabled={!hasDomainPermission}
              >
                {capitalize(
                  intl.formatMessage({ id: "settings.domains.delete" })
                )}
              </Button>
            </>
          </ButtonWrapper>
        </S.AddDomainFormLayout>
      </S.Container>

      <Modal
        isOpen={showDeleteModal && !isProductionEnvironment}
        onOpenChange={closeModals}
        handleClose={closeModals}
        id="delete-modal-domain"
      >
        <Typography.Title tag="h2">
          {intl.formatMessage({
            id: "settings.domains.modal.delete.title"
          })}
        </Typography.Title>
        <Typography.BodyText>
          {intl.formatMessage(
            {
              id: "settings.domains.delete.domain"
            },
            {
              domainName: <strong>{customDomain}</strong>
            }
          )}
        </Typography.BodyText>
        <ButtonWrapper spacing="modal" justifyContent="end">
          <Button variant="secondary" onClick={closeModals} analyticId="cancel">
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button
            id="delete-domain-confirm-modal-button"
            onClick={() => {
              if (environmentDomain) {
                handleDelete(environmentDomain);
              }
              closeModals();
              customDomainsView();
            }}
            analyticId="settings.domains.modal.delete.confirm"
          >
            {intl.formatMessage({
              id: "settings.domains.modal.delete.confirm"
            })}
          </Button>
        </ButtonWrapper>
      </Modal>

      {showDeleteModal && isProductionEnvironment && (
        <ModalConfirmDelete
          closeModal={closeModals}
          deleteEnvironmentDomain={handleDelete}
          domain={environmentDomain}
          isOpen={showDeleteModal}
          showingWarning={
            environmentDomains?.length !== 0 && isProductionEnvironment
          }
        />
      )}
    </S.Layout>
  );
};

export default EnvironmentDomainView;
